import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Drawer,
} from '@mui/material';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  getOrganizationTechnologyComplete,
  patchEnterpriseContext,
  saveTechnologyLandscape,
} from '../../services/commonService';
import {
  updateEnterpriseContext,
  updateEnterpriseContext as updateEnterpriseContextAction,
} from '../../slices/enterpriseContextSlice/enterpriseContextSlice';
import { RootState } from '../../store';
import CompanyForm from './CompanyForm';
import DataLandscapeForm from './DataLandscapeForm';
import {
  //DataLandScape,
  EnterpriseContext,
  EnterpriseContextState,
  TechnologyLandscapeResponse,
} from './EnterpriseContextInterfaces';
import ConfirmationModal from '../../common/components/ConfirmationModal';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  discardChangesMessage,
  saveChangesMessage,
  saveEnterpriseDataMessage,
} from '../../appConstants';
import { Snackbar, Alert } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';

interface TechnologyDataLandscapeProps {
  minContentHeight?: string;
  maxContentHeight?: string;
  drawerContainer?: HTMLElement | null;
}

const TechnologyDataLandscape: React.FC<TechnologyDataLandscapeProps> = ({
  minContentHeight = '82vh',
  maxContentHeight = '82vh',
  drawerContainer,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const enterpriseContext = useSelector((state: RootState) => state.enterpriseContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialActiveComponent = () => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['companyForm', 'dataLandscapeForm'];
    return validTabs.includes(tabParam || '') ? tabParam : 'companyForm';
  };

  const [enterpriseContextPayload, setEnterpriseContextPayload] = useState<
    EnterpriseContextState | undefined
  >(enterpriseContext);
  const [activeComponent, setActiveComponent] = useState(getInitialActiveComponent);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [selectedIndustryId, setSelectedIndustryId] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedOption] = useState<string | null>(null);

  // Add state for tracking technology landscape changes
  const [technologyLandscapeData, setTechnologyLandscapeData] = useState<any>(null);
  const [genAIStack, setGenAIStack] = useState<string[]>([]);
  const [genAIOthers, setGenAIOthers] = useState<string[]>([]);
  const [dataLake, setDataLake] = useState<string[]>([]);
  const [cloudProvider, setCloudProvider] = useState<string[]>([]);

  const originalDataRef = useRef<EnterpriseContextState | undefined>(undefined);
  const isNavigatingRef = useRef<boolean>(false);
  const navigationBlockerRef = useRef<(() => void) | null>(null);
  const rootPersist = localStorage.getItem('persist:root');
  let isAdmin = false;
  if (rootPersist) {
    try {
      const parsedRoot = JSON.parse(rootPersist);
      if (parsedRoot.user) {
        const userObj = JSON.parse(parsedRoot.user);
        if (userObj.username === 'admin') {
          isAdmin = true;
        }
      }
    } catch (err) {
      console.error('Error parsing persist:root', err);
    }
  }
  const EMPTY_ORG_ID = 'Empty';
  const LOCAL_STORAGE_ORG_KEY = 'current_organization';

  // Get current organization from localStorage
  const getCurrentOrganizationId = useCallback((): string => {
    return localStorage.getItem(LOCAL_STORAGE_ORG_KEY) || EMPTY_ORG_ID;
  }, []);

  const organizationId = getCurrentOrganizationId();

  // Block navigation when there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) {
      if (navigationBlockerRef.current) {
        navigationBlockerRef.current();
        navigationBlockerRef.current = null;
      }
      return;
    }

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const blockNavigation = (targetUrl: string) => {
      if (isNavigatingRef.current) {
        return true;
      }

      const targetPath = new URL(targetUrl, window.location.origin).pathname;
      const currentPath = location.pathname;

      if (targetPath !== currentPath) {
        setPendingNavigation(targetPath);
        setIsSaveModalOpen(true);
        return false;
      }

      return true;
    };

    window.history.pushState = function (state: any, title: string, url?: string | URL | null) {
      if (url && !blockNavigation(url.toString())) {
        return;
      }
      return originalPushState.call(this, state, title, url);
    };

    window.history.replaceState = function (state: any, title: string, url?: string | URL | null) {
      if (url && !blockNavigation(url.toString())) {
        return;
      }
      return originalReplaceState.call(this, state, title, url);
    };

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges && !isNavigatingRef.current) {
        event.preventDefault();
        window.history.pushState(null, '', location.pathname);
        setPendingNavigation(document.location.pathname);
        setIsSaveModalOpen(true);
      }
    };

    window.addEventListener('popstate', handlePopState);

    navigationBlockerRef.current = () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };

    return () => {
      if (navigationBlockerRef.current) {
        navigationBlockerRef.current();
        navigationBlockerRef.current = null;
      }
    };
  }, [hasUnsavedChanges, location.pathname]);

  // Handle React Router navigation
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleLinkClick = (event: Event) => {
      if (isNavigatingRef.current) return;

      const target = event.target as HTMLElement;
      const link = target.closest('a[href]');

      if (link) {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/') && href !== location.pathname) {
          event.preventDefault();
          event.stopPropagation();
          setPendingNavigation(href);
          setIsSaveModalOpen(true);
        }
      }
    };

    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [hasUnsavedChanges, location.pathname]);

  const handleCompanyDetailsChange = <K extends keyof EnterpriseContext>(
    value: EnterpriseContext[K],
    fieldName: K
  ) => {
    if (!enterpriseContextPayload) return;

    const updatedEnterpriseContext: EnterpriseContext = {
      ...enterpriseContextPayload.enterpriseContext,
      [fieldName]: value,
    };

    const updatedPayload: EnterpriseContextState = {
      ...enterpriseContextPayload,
      enterpriseContext: updatedEnterpriseContext,
      enterprise_context: updatedEnterpriseContext,
    };
    setHasUnsavedChanges(true);
    setEnterpriseContextPayload(updatedPayload);
  };

  // const handleDataLandScapeChange = (
  //   updates: Partial<DataLandScape>,
  //   index: number
  // ) => {
  //   if (!enterpriseContextPayload) return;

  //   const updatedDataLandScape: DataLandScape[] = [
  //     ...enterpriseContextPayload.enterpriseContext.data_landscape,
  //   ];

  //   const current = { ...updatedDataLandScape[index], ...updates };
  //   updatedDataLandScape[index] = current;

  //   const updatedEnterpriseContext: EnterpriseContext = {
  //     ...enterpriseContextPayload.enterpriseContext,
  //     data_landscape: updatedDataLandScape,
  //   };

  //   const updatedPayload: EnterpriseContextState = {
  //     ...enterpriseContextPayload,
  //     enterpriseContext: updatedEnterpriseContext,
  //     enterprise_context: updatedEnterpriseContext,
  //   };

  //   setEnterpriseContextPayload(updatedPayload);
  //   dispatch(updateEnterpriseContext(updatedPayload));
  // };

  // Add platform functionality
  // const handleAddPlatform = (platformData: DataLandScape) => {
  //   if (!enterpriseContextPayload) return;

  //   const updatedDataLandScape: DataLandScape[] = [
  //     ...enterpriseContextPayload.enterpriseContext.data_landscape,
  //     platformData,
  //   ];

  //   const updatedEnterpriseContext: EnterpriseContext = {
  //     ...enterpriseContextPayload.enterpriseContext,
  //     data_landscape: updatedDataLandScape,
  //   };

  //   const updatedPayload: EnterpriseContextState = {
  //     ...enterpriseContextPayload,
  //     enterpriseContext: updatedEnterpriseContext,
  //     enterprise_context: updatedEnterpriseContext,
  //   };

  //   setEnterpriseContextPayload(updatedPayload);
  // };

  // Delete platform functionality
  // const handleDeletePlatform = (index: number) => {
  //   if (!enterpriseContextPayload) return;

  //   const updatedDataLandScape: DataLandScape[] = [
  //     ...enterpriseContextPayload.enterpriseContext.data_landscape,
  //   ];

  //   updatedDataLandScape.splice(index, 1);

  //   const updatedEnterpriseContext: EnterpriseContext = {
  //     ...enterpriseContextPayload.enterpriseContext,
  //     data_landscape: updatedDataLandScape,
  //   };

  //   const updatedPayload: EnterpriseContextState = {
  //     ...enterpriseContextPayload,
  //     enterpriseContext: updatedEnterpriseContext,
  //     enterprise_context: updatedEnterpriseContext,
  //   };

  //   setEnterpriseContextPayload(updatedPayload);
  // };

  // Function to handle technology landscape data changes
  const handleTechnologyLandscapeChange = (data: any) => {
    setTechnologyLandscapeData(data);

    // Store technology landscape data in redux immediately for persistence
    if (enterpriseContextPayload) {
      const updatedEnterpriseContext: EnterpriseContext = {
        ...enterpriseContextPayload.enterpriseContext,
        technology_landscape: data,
      };
      const updatedPayload: EnterpriseContextState = {
        ...enterpriseContextPayload,
        enterpriseContext: updatedEnterpriseContext,
        enterprise_context: updatedEnterpriseContext,
      };
      setEnterpriseContextPayload(updatedPayload);
      dispatch(updateEnterpriseContext(updatedPayload));
    }
  };

  const handleClearForm = () => {
    if (!enterpriseContextPayload) return;

    // Preserve current industry data and other fields, only clear description
    const clearedEnterpriseContext: EnterpriseContextState = {
      ...enterpriseContextPayload,
      enterpriseContext: {
        ...enterpriseContextPayload.enterpriseContext,
        description: '',
        industry: isAdmin ? [] : (enterpriseContextPayload.enterpriseContext?.industry ?? []),
        country: enterpriseContextPayload.enterpriseContext?.country ?? '',
        revenue: enterpriseContextPayload.enterpriseContext?.revenue ?? '',
        employee: enterpriseContextPayload.enterpriseContext?.employee ?? '',
        information: enterpriseContextPayload.enterpriseContext?.information ?? [],
        topProducts: enterpriseContextPayload.enterpriseContext?.topProducts ?? [],
        topServices: enterpriseContextPayload.enterpriseContext?.topServices ?? [],
        data_landscape: enterpriseContextPayload.enterpriseContext?.data_landscape ?? [],
        technology_landscape:
          enterpriseContextPayload.enterpriseContext?.technology_landscape ?? [],
      },
      enterprise_context: {
        ...enterpriseContextPayload.enterprise_context,
        description: '',
        industry: isAdmin ? [] : (enterpriseContextPayload.enterprise_context?.industry ?? []),
        country: enterpriseContextPayload.enterprise_context?.country ?? '',
        revenue: enterpriseContextPayload.enterprise_context?.revenue ?? '',
        employee: enterpriseContextPayload.enterprise_context?.employee ?? '',
        //industry: enterpriseContextPayload.enterprise_context?.industry ?? [],
        information: enterpriseContextPayload.enterprise_context?.information ?? [],
        topProducts: enterpriseContextPayload.enterprise_context?.topProducts ?? [],
        topServices: enterpriseContextPayload.enterprise_context?.topServices ?? [],
        data_landscape: enterpriseContextPayload.enterprise_context?.data_landscape ?? [],
        technology_landscape:
          enterpriseContextPayload.enterprise_context?.technology_landscape ?? [],
      },
    };

    setEnterpriseContextPayload(clearedEnterpriseContext);

    originalDataRef.current = clearedEnterpriseContext;
    dispatch(updateEnterpriseContext(clearedEnterpriseContext));
    setTechnologyLandscapeData(null); // Reset technology landscape changes
    setHasUnsavedChanges(false);
  };

  const handleSaveForm = async () => {
    if (!enterpriseContextPayload) return;

    setIsSaving(true);
    try {
      // Check which component is active to determine save logic
      if (activeComponent === 'dataLandscapeForm' && technologyLandscapeData) {
        // For technology landscape - call saveTechnologyLandscape API with organizationId
        let technology_infra_payload = {
          cloud_provider: cloudProvider || [],
          gen_ai_stask: genAIStack || [],
          data_lake: dataLake || [],
        };
        let payload = { ...technologyLandscapeData, technology_infra: technology_infra_payload };
        let technology_infra = {
          cloud_provider: cloudProvider || [],
          gen_ai_stask: genAIStack || [],
          data_lake: dataLake || [],
        };
        await saveTechnologyLandscape(payload, organizationId);

        if ((window as any).solutionSyncCache) {
          (window as any).solutionSyncCache.clear();
        }
        if ((window as any).agentDataCache) {
          (window as any).agentDataCache.clear();
        }
        
        // Update original data reference to include the saved technology landscape
        const updatedOriginalData: EnterpriseContextState = {
          ...enterpriseContextPayload,
          enterpriseContext: {
            ...enterpriseContextPayload.enterpriseContext,
            technology_landscape: technologyLandscapeData,
            country: enterpriseContextPayload.enterpriseContext.country ?? '',
            revenue: enterpriseContextPayload.enterpriseContext.revenue ?? '',
            employee: enterpriseContextPayload.enterpriseContext.employee ?? '',
            description: enterpriseContextPayload.enterpriseContext.description ?? '',
            technology_infra: technology_infra,
          },
          enterprise_context: {
            ...enterpriseContextPayload.enterprise_context,
            technology_landscape: technologyLandscapeData,
            country: enterpriseContextPayload.enterprise_context?.country ?? '',
            revenue: enterpriseContextPayload.enterprise_context?.revenue ?? '',
            employee: enterpriseContextPayload.enterprise_context?.employee ?? '',
            description: enterpriseContextPayload.enterprise_context?.description ?? '',
            industry: enterpriseContextPayload.enterprise_context?.industry ?? [],
            information: enterpriseContextPayload.enterprise_context?.information ?? [],
            topProducts: enterpriseContextPayload.enterprise_context?.topProducts ?? [],
            topServices: enterpriseContextPayload.enterprise_context?.topServices ?? [],
            data_landscape: enterpriseContextPayload.enterprise_context?.data_landscape ?? [],
            technology_infra: technology_infra,
          },
        };
        originalDataRef.current = updatedOriginalData;
      } else {
        // For company form - call patchEnterpriseContext API
        dispatch(updateEnterpriseContext(enterpriseContextPayload));
        await patchEnterpriseContext(
          { description: enterpriseContextPayload.enterpriseContext.description },
          organizationId
        );
        console.log('Enterprise context saved successfully', enterpriseContextPayload);
        originalDataRef.current = { ...enterpriseContextPayload };
      }

      setHasUnsavedChanges(false);
      setTechnologyLandscapeData(null); // Reset after successful save
      setOpenSnackbar(true);

      // Close modal if it was open due to navigation attempt
      if (isSaveModalOpen) {
        setIsSaveModalOpen(false);
      }

      // If there's a pending navigation after save, execute it
      if (pendingNavigation) {
        isNavigatingRef.current = true;
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      // Handle error appropriately
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    // Reset to original data
    if (originalDataRef.current) {
      setEnterpriseContextPayload({ ...originalDataRef.current });
      dispatch(updateEnterpriseContext(originalDataRef.current));
    }

    setHasUnsavedChanges(false);
    setTechnologyLandscapeData(null); // Reset technology landscape changes
    setIsSaveModalOpen(false);

    // Execute pending navigation after discarding changes
    if (pendingNavigation) {
      isNavigatingRef.current = true;
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const renderComponent = () => {
    switch (activeComponent) {
      case 'companyForm':
        return (
          <CompanyForm
            enterpriseContextPayload={enterpriseContextPayload}
            handleCompanyDetailsChange={handleCompanyDetailsChange}
            // selectedIndustryId={selectedIndustryId}
            isAdmin={isAdmin}
          />
        );
      case 'dataLandscapeForm':
        return (
          <DataLandscapeForm
            onTechnologyDataChange={handleTechnologyLandscapeChange}
            setIsDirty={setHasUnsavedChanges}
            genAIStack={genAIStack}
            genAIOthers={genAIOthers}
            dataLake={dataLake}
            cloudProvider={cloudProvider}
            setGenAIStack={setGenAIStack}
            setGenAIOthers={setGenAIOthers}
            setCloudProvider={setCloudProvider}
            setDataLake={setDataLake}
          />
        );
      default:
        return (
          <CompanyForm
            enterpriseContextPayload={enterpriseContextPayload}
            handleCompanyDetailsChange={handleCompanyDetailsChange}
            selectedIndustryId={selectedIndustryId}
          />
        );
    }
  };

  const handleChangeActiveTab = (tab: string) => {
    setActiveComponent(tab);
    setSearchParams({ tab });
  };

  const handleSecondaryAction = () => {
    handleDiscardChanges();
  };

  const handleModalClose = () => {
    // Only allow closing if not in the middle of navigation
    if (!pendingNavigation || !selectedOption) {
      setIsSaveModalOpen(false);
    }
  };

  // Fetch technology landscape data on component mount
  useEffect(() => {
    const fetchTechnologyLandscape = async () => {
      const orgId = getCurrentOrganizationId();
      if (orgId && orgId !== EMPTY_ORG_ID) {
        try {
          const apiResult = await getOrganizationTechnologyComplete(orgId);
          const response: TechnologyLandscapeResponse = apiResult ?? {
            data: {},
            selected_industry: '',
            functions: false,
            content: '',
            description: '',
          };
          console.log('Fetched technology landscape:', response);

          // Update description if available
          if (response?.description) {
            setEnterpriseContextPayload((prevPayload) => {
              if (!prevPayload) return undefined;
              const prevContext = prevPayload.enterpriseContext || {};
              const updatedEnterpriseContext = {
                ...prevContext,
                description: response.description ?? '',
              };
              return {
                ...prevPayload,
                enterpriseContext: updatedEnterpriseContext,
                enterprise_context: updatedEnterpriseContext,
                id: prevPayload.id || '',
                createdAt: prevPayload.createdAt,
                updatedAt: prevPayload.updatedAt,
              };
            });
          }

          // Handle selected industry - this is the key condition
          if (response?.selected_industry) {
            // Industry is available from GET API
            setSelectedIndustryId(response.selected_industry);
          } else {
            // No selected_industry from GET API - user will need to select and it will be saved via PATCH
            setSelectedIndustryId(null);
            console.log('No selected industry from API - user will need to select one');
          }

          // Handle the main technology landscape data
          if (response) {
            setTechnologyLandscapeData(response?.industry_data);
            setGenAIStack(response?.technology_infra?.gen_ai_stask || []);
            setCloudProvider(response?.technology_infra?.cloud_provider || []);
            setDataLake(response?.technology_infra?.data_lake || []);
            let industryArray: string[] = [];
            if (response?.selected_industry) {
              industryArray = [response.selected_industry];
            } else if (response?.data?.industry && Array.isArray(response.data.industry)) {
              industryArray = response.data.industry;
            }

            const updatedPayload: EnterpriseContextState = {
              id: enterpriseContext?.id || response.data?.id || '',
              createdAt: enterpriseContext?.createdAt || response.data?.createdAt || Date.now(),
              updatedAt: enterpriseContext?.updatedAt || response.data?.updatedAt || Date.now(),
              enterpriseContext: {
                country:
                  enterpriseContext?.enterpriseContext?.country ||
                  response.data?.enterpriseContext?.country ||
                  '',
                revenue:
                  enterpriseContext?.enterpriseContext?.revenue ||
                  response.data?.enterpriseContext?.revenue ||
                  '',
                employee:
                  enterpriseContext?.enterpriseContext?.employee ||
                  response.data?.enterpriseContext?.employee ||
                  '',
                industry: industryArray,
                description:
                  response.description ||
                  enterpriseContext?.enterpriseContext?.description ||
                  response.data?.enterpriseContext?.description ||
                  '',
                information:
                  enterpriseContext?.enterpriseContext?.information ||
                  response.data?.enterpriseContext?.information ||
                  [],
                topProducts:
                  enterpriseContext?.enterpriseContext?.topProducts ||
                  response.data?.enterpriseContext?.topProducts ||
                  [],
                topServices:
                  enterpriseContext?.enterpriseContext?.topServices ||
                  response.data?.enterpriseContext?.topServices ||
                  [],
                data_landscape:
                  enterpriseContext?.enterpriseContext?.data_landscape ||
                  response.data?.enterpriseContext?.data_landscape ||
                  [],
                technology_landscape: { industry: response?.industry_data || response.data || [] },
                technology_infra: {
                  cloud_provider: response?.technology_infra?.cloud_provider || '',
                  gen_ai_stask: response?.technology_infra?.gen_ai_stask || [],
                  data_lake: response?.technology_infra?.data_lake || '',
                },
              },
              enterprise_context: {
                country:
                  enterpriseContext?.enterprise_context?.country ||
                  response.data?.enterprise_context?.country ||
                  '',
                revenue:
                  enterpriseContext?.enterprise_context?.revenue ||
                  response.data?.enterprise_context?.revenue ||
                  '',
                employee:
                  enterpriseContext?.enterprise_context?.employee ||
                  response.data?.enterprise_context?.employee ||
                  '',
                industry: industryArray,
                description:
                  response.description ||
                  enterpriseContext?.enterprise_context?.description ||
                  response.data?.enterprise_context?.description ||
                  '',
                information:
                  enterpriseContext?.enterprise_context?.information ||
                  response.data?.enterprise_context?.information ||
                  [],
                topProducts:
                  enterpriseContext?.enterprise_context?.topProducts ||
                  response.data?.enterprise_context?.topProducts ||
                  [],
                topServices:
                  enterpriseContext?.enterprise_context?.topServices ||
                  response.data?.enterprise_context?.topServices ||
                  [],
                data_landscape:
                  enterpriseContext?.enterprise_context?.data_landscape ||
                  response.data?.enterprise_context?.data_landscape ||
                  [],
                technology_landscape: { industry: response?.industry_data || response.data || [] },
                technology_infra: {
                  cloud_provider: response?.technology_infra?.cloud_provider || '',
                  gen_ai_stask: response?.technology_infra?.gen_ai_stask || [],
                  data_lake: response?.technology_infra?.data_lake || '',
                },
              },
            };

            // Set original data reference after setting the payload
            setTimeout(() => {
              originalDataRef.current = { ...updatedPayload };
            }, 0);

            // Update enterprise context payload with fetched data
            setEnterpriseContextPayload(updatedPayload);

            // Update Redux store
            dispatch(updateEnterpriseContextAction(updatedPayload));
          }
        } catch (error) {
          console.error('Error fetching organization technology landscape:', error);
          // Set selectedIndustryId to null if API call fails
          setSelectedIndustryId(null);
        }
      }
    };

    fetchTechnologyLandscape();
  }, [dispatch, getCurrentOrganizationId]); // Removed enterpriseContextPayload dependency to avoid infinite loop

  const [stepSheetOpen, setStepSheetOpen] = useState(false);

  // Handle URL parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['companyForm', 'dataLandscapeForm'];

    if (validTabs.includes(tabParam || '')) {
      setActiveComponent(tabParam || 'companyForm');
    }
  }, [searchParams]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {isMobile ? (
        <>
          {/* Title */}
          <Box
            sx={{
              padding: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#fff',
            }}
          >
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: 600, fontSize: '1rem' }}
            >
              Enterprise Context
              {hasUnsavedChanges && (
                <Typography
                  component="span"
                  sx={{ color: 'warning.main', fontSize: '0.75rem', ml: 1 }}
                >
                  (Unsaved)
                </Typography>
              )}
            </Typography>
          </Box>

          {/* Mobile step selector */}
          <Box
            onClick={() => setStepSheetOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              px: 2,
              py: 1,
              m: 2,
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: 'primary.main' }}>
              {activeComponent === 'companyForm' ? 'Company Info' : 'Technology / Data Landscape'}
            </Typography>
            <ArrowDropDown />
          </Box>

          {/* Drawer for step list */}
          <Drawer
            anchor="bottom"
            open={stepSheetOpen}
            onClose={() => setStepSheetOpen(false)}
            container={drawerContainer || undefined}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                p: 2,
              },
            }}
          >
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {[
                { key: 'companyForm', label: 'Company Info' },
                { key: 'dataLandscapeForm', label: 'Technology / Data Landscape' },
              ].map((item) => (
                <Box
                  key={item.key}
                  onClick={() => {
                    handleChangeActiveTab(item.key);
                    setStepSheetOpen(false);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor:
                      activeComponent === item.key ? 'rgba(38,83,248,0.06)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Drawer>

          {/* Component content */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>{renderComponent()}</Box>

          {/* Sticky Footer */}
          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#ffffff',
              py: 2,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {activeComponent === 'companyForm' && (
              <Button
                variant="outlined"
                onClick={handleClearForm}
                disabled={isSaving}
                sx={{ px: 2, py: 1, textTransform: 'none', fontWeight: 500, mr: 2 }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {/* Fixed Sidebar */}
          <Paper
            elevation={0}
            sx={{
              width: isMobile ? 240 : 300,
              borderRight: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              borderRadius: 0,
              backgroundColor: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                padding: 2,
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                color="primary"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Enterprise Context
                {hasUnsavedChanges && (
                  <Typography
                    component="span"
                    sx={{
                      color: 'warning.main',
                      fontSize: '0.75rem',
                      ml: 1,
                    }}
                  >
                    (Unsaved)
                  </Typography>
                )}
              </Typography>
            </Box>

            <List component="nav" sx={{ flexGrow: 1, py: 0, overflow: 'hidden' }}>
              <ListItemButton
                selected={activeComponent === 'companyForm'}
                onClick={() => handleChangeActiveTab('companyForm')}
                sx={{
                  py: 2,
                  bgcolor: activeComponent === 'companyForm' ? '#1A55F9' : 'inherit',
                  color: activeComponent === 'companyForm' ? '#FFFFFF' : 'inherit',
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: activeComponent === 'companyForm' ? '#FFFFFF' : 'inherit',
                  },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: activeComponent === 'companyForm' ? '#1A55F9' : 'inherit',
                    color: activeComponent === 'companyForm' ? '#FFFFFF' : 'inherit',
                    backgroundColor: activeComponent === 'companyForm' ? '#1A55F9' : 'inherit',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: '28px' }}>
                  <img
                    src="/images/company-form.svg"
                    alt="company-form"
                    width={20}
                    height={20}
                    style={{
                      filter:
                        activeComponent === 'companyForm' ? 'brightness(0) invert(1)' : 'none',
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Company Info" />
              </ListItemButton>

              <ListItemButton
                selected={activeComponent === 'dataLandscapeForm'}
                onClick={() => handleChangeActiveTab('dataLandscapeForm')}
                sx={{
                  py: 2,
                  bgcolor: activeComponent === 'dataLandscapeForm' ? '#1A55F9' : 'inherit',
                  color: activeComponent === 'dataLandscapeForm' ? '#FFFFFF' : 'inherit',
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: activeComponent === 'dataLandscapeForm' ? '#FFFFFF' : 'inherit',
                  },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: activeComponent === 'dataLandscapeForm' ? '#1A55F9' : 'inherit',
                    color: activeComponent === 'dataLandscapeForm' ? '#FFFFFF' : 'inherit',
                    backgroundColor:
                      activeComponent === 'dataLandscapeForm' ? '#1A55F9' : 'inherit',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: '28px' }}>
                  <img
                    src="/images/techlogy-data-landscape.svg"
                    alt="techlogy-data-landscape"
                    width={20}
                    height={20}
                    style={{
                      filter:
                        activeComponent === 'dataLandscapeForm'
                          ? 'brightness(0) invert(1)'
                          : 'none',
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Technology/ Data Landscape" />
              </ListItemButton>
            </List>
          </Paper>

          {/* Right Container */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              height: '100%', // take up available modal height
              overflow: 'hidden', // prevent full-container scrolling
            }}
          >
            {/* Scrollable Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'scroll', // only this section scrolls
                px: 3,
                py: 2,
                minHeight: minContentHeight, // critical: allows flex child to shrink properly
                maxHeight: maxContentHeight,
              }}
            >
              {renderComponent()}
            </Box>

            {/* Sticky Footer */}
            <Box
              sx={{
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#ffffff',
                py: 2,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {activeComponent === 'companyForm' && (
                <Button
                  variant="outlined"
                  onClick={handleClearForm}
                  disabled={isSaving}
                  sx={{
                    px: isMobile ? 2 : 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    mr: 2,
                  }}
                >
                  Clear
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveForm}
                disabled={isSaving}
                sx={{ mr: 1 }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isSaveModalOpen}
        title={`Save Changes`}
        description={
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutlineIcon color="warning" />
            <Typography sx={{ color: '#4f4f4f' }}>{saveEnterpriseDataMessage}</Typography>
          </Box>
        }
        primaryButtonText={
          <Box display="flex" alignItems="center" gap={1}>
            <CheckIcon fontSize="small" />
            <span>{saveChangesMessage}</span>
          </Box>
        }
        secondaryButtonText={
          <Box display="flex" alignItems="center" gap={1}>
            <ClearIcon fontSize="small" />
            <span>{discardChangesMessage}</span>
          </Box>
        }
        onPrimaryAction={handleSaveForm}
        onClose={handleModalClose}
        onSecondaryAction={handleSecondaryAction}
        width={'sm'}
        outerPadding={0}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Data saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechnologyDataLandscape;
